// Pricing calculations matching legacy PricesHelper.cs
// Base pricing rates (these would typically come from a pricing API or database)
const BASE_PRICES = {
  // Lawn rates (per hour)
  lawn: {
    basic: 130,    // $130/hour basic rate
    premium: 160   // $160/hour premium rate
  },
  
  // Gardening rates (per hour)
  gardening: {
    basic: 130,    // $130/hour basic rate
    premium: 160   // $160/hour premium rate
  },
  
  // Cleanup rates (per hour)
  springClean: {
    basic: 130     // $130/hour for spring cleanup
  },
  
  fallCleanup: {
    basic: 130     // $130/hour for fall cleanup
  },
  
  // Trimming rates (per hour)
  trimming: {
    basic: 130     // $130/hour for trimming
  },
  
  // Trash bag rates
  trashBag: {
    onSite: 5.50,   // $5.50 per bag disposed on site
    offSite: 7.25,  // $7.25 per bag disposed off site
    extraOnSite: 6.00,
    extraOffSite: 8.00
  },
  
  // Default markup percentages
  markup: {
    aeration: 40,   // 40% markup
    grub: 40,       // 40% markup
    simple3: 40,    // 40% markup
    hybrid5: 40,    // 40% markup
    premium6: 40,   // 40% markup
    ultra6: 40      // 40% markup
  }
};

// Helper functions
export const calcWeeks = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
  const days = Math.max(0, Math.ceil((end - start) / (1000 * 60 * 60 * 24)));
  return Math.ceil(days / 7);
};

export const toRoundDecimal = (value) => {
  return Math.round(value * 100) / 100;
};

export const increaseCustomPercentage = (amount, isActive = false, percentage = 0) => {
  if (!isActive || !percentage) return amount;
  return amount * (1 + (percentage / 100));
};

// Lawn Calculations
export const calculateLawnWeeklyPrice = (services, propertyInfo = {}) => {
  const { lawn } = services;
  if (!lawn.enabled || !lawn.startDate || !lawn.endDate) return 0;
  
  let weeklyPrice = 0;
  
  // Base mowing price
  if (lawn.mowing) {
    const hourlyRate = lawn.maintenance === 'Premium' ? BASE_PRICES.lawn.premium : BASE_PRICES.lawn.basic;
    weeklyPrice += (hourlyRate / 60) * lawn.timeMinutes;
  }
  
  // Add bagging if enabled
  if (lawn.bagging) {
    weeklyPrice += lawn.weekPriceBagging || 0;
  }
  
  return toRoundDecimal(weeklyPrice);
};

export const calculateLawnSeasonalPrice = (services, propertyInfo = {}) => {
  const weeks = calcWeeks(services.lawn.startDate, services.lawn.endDate);
  const weeklyPrice = calculateLawnWeeklyPrice(services, propertyInfo);
  const trashBagPrice = calculateLawnTrashBag(services);
  
  return toRoundDecimal((weeklyPrice * weeks) + trashBagPrice);
};

export const calculateLawnTrashBag = (services) => {
  const { lawn } = services;
  if (!lawn.trashBagServiceEnabled) return 0;
  
  const weeks = calcWeeks(lawn.startDate, lawn.endDate);
  const pricePerBag = lawn.trashBagType === 'OnSite' ? BASE_PRICES.trashBag.onSite : BASE_PRICES.trashBag.offSite;
  const weeklyBags = lawn.trashBagQuantity || 0;
  
  return toRoundDecimal(weeks * weeklyBags * pricePerBag);
};

// Gardening Calculations
export const calculateGardeningWeeklyPrice = (services) => {
  const { gardening } = services;
  if (!gardening.enabled) return 0;
  
  const hourlyRate = gardening.tariff === 'Premium' ? BASE_PRICES.gardening.premium : BASE_PRICES.gardening.basic;
  const ratePerMinute = hourlyRate / 60;
  
  let weeklyPrice = 0;
  
  // De-weeding
  if (gardening.deWeeding === 1) { // Weekly
    weeklyPrice += ratePerMinute * gardening.deWeedingTime;
  } else if (gardening.deWeeding === 2) { // Bi-weekly
    weeklyPrice += (ratePerMinute * gardening.deWeedingTime) / 2;
  }
  
  // Soil Tilling
  if (gardening.soilTilling === 1) { // Weekly
    weeklyPrice += ratePerMinute * gardening.soilTillingTime;
  } else if (gardening.soilTilling === 2) { // Bi-weekly
    weeklyPrice += (ratePerMinute * gardening.soilTillingTime) / 2;
  }
  
  // Edge Maintenance
  if (gardening.edge === 1) { // Weekly
    weeklyPrice += ratePerMinute * gardening.edgeTime;
  } else if (gardening.edge === 2) { // Bi-weekly
    weeklyPrice += (ratePerMinute * gardening.edgeTime) / 2;
  }
  
  return toRoundDecimal(weeklyPrice);
};

export const calculateGardeningSeasonalPrice = (services) => {
  const weeks = calcWeeks(services.gardening.startDate, services.gardening.endDate);
  const weeklyPrice = calculateGardeningWeeklyPrice(services);
  let seasonalPrice = weeklyPrice * weeks;
  
  // Add weed spray if selected
  if (services.gardening.weedSelected) {
    seasonalPrice += services.gardening.weedSprayTime || 0;
  }
  
  // Add mulch if selected
  if (services.gardening.mulchSelected) {
    seasonalPrice += services.gardening.mulchTotalPrice || 0;
  }
  
  return toRoundDecimal(seasonalPrice);
};

// Spring Cleanup Calculations
export const calculateSpringPrice = (services) => {
  const { spring } = services;
  if (!spring.enabled || !spring.workSelected) return 0;
  
  const hourlyRate = BASE_PRICES.springClean.basic;
  const timePrice = (hourlyRate / 60) * spring.timeMinutes;
  
  return toRoundDecimal(spring.disposalFee + timePrice);
};

// Fall Cleanup Calculations
export const calculateFallPrice = (services) => {
  const { fall } = services;
  if (!fall.enabled || !fall.selected) return 0;
  
  const hourlyRate = BASE_PRICES.fallCleanup.basic;
  const timePrice = (hourlyRate / 60) * fall.timeMinutes;
  
  return toRoundDecimal(fall.disposalFee + timePrice);
};

// Trimming Calculations
export const calculateTrimmingPrice = (services) => {
  const { trimming } = services;
  if (!trimming.enabled || !trimming.selected) return 0;
  
  const hourlyRate = BASE_PRICES.trimming.basic;
  const timePrice = (hourlyRate / 60) * trimming.timeMinutes * trimming.applications;
  
  return toRoundDecimal(trimming.disposalFee + timePrice);
};

// Turf Program Calculations
export const calculateTurfPrice = (services, propertyInfo = {}) => {
  const { turf } = services;
  if (!turf.enabled) return 0;
  
  const squareFt = propertyInfo.squareFt || 1000; // Default to 1000 sq ft if not provided
  let totalPrice = 0;
  
  // Base turf pricing calculation (simplified - real calculation is complex)
  const baseTurfPrice = squareFt * 0.50; // $0.50 per sq ft base
  
  // Program pricing
  if (turf.simple3Selected) {
    totalPrice += baseTurfPrice * (turf.simple3MU / 100);
  }
  if (turf.hybrid5Selected) {
    totalPrice += baseTurfPrice * (turf.hybrid5MU / 100);
  }
  if (turf.premium6Selected) {
    totalPrice += baseTurfPrice * (turf.premium6MU / 100);
  }
  if (turf.ultra6Selected) {
    totalPrice += baseTurfPrice * (turf.ultra6MU / 100);
  }
  
  // Add-on services
  if (turf.grubControl) {
    totalPrice += turf.grubPrice || (squareFt * 0.15); // $0.15 per sq ft for grub control
  }
  if (turf.aeration) {
    totalPrice += turf.aerationPrice || (squareFt * 0.25); // $0.25 per sq ft for aeration
  }
  
  return toRoundDecimal(totalPrice);
};

// Snow Removal Calculations
export const calculateSnowPrice = (services, propertyInfo = {}) => {
  const { snow } = services;
  if (!snow.enabled || snow.program === 0) return 0;
  
  let totalPrice = 0;
  
  // Base program price
  if ([1, 2, 3].includes(snow.program)) { // Basic, Premium, Premium + Salt
    totalPrice += snow.sum || 0;
  }
  
  // Additional service calculations would go here
  // (simplified for now - real calculations are very complex)
  
  return toRoundDecimal(totalPrice);
};

// Total Quote Calculation
export const calculateQuoteTotal = (services, propertyInfo = {}) => {
  let total = 0;
  
  total += calculateLawnSeasonalPrice(services, propertyInfo);
  total += calculateGardeningSeasonalPrice(services);
  total += calculateSpringPrice(services);
  total += calculateFallPrice(services);
  total += calculateTrimmingPrice(services);
  total += calculateTurfPrice(services, propertyInfo);
  total += calculateSnowPrice(services, propertyInfo);
  
  return toRoundDecimal(total);
};

// Individual service price getters for real-time display
export const getPriceForService = (serviceType, services, propertyInfo = {}) => {
  switch (serviceType) {
    case 'lawn':
      return calculateLawnSeasonalPrice(services, propertyInfo);
    case 'gardening':
      return calculateGardeningSeasonalPrice(services);
    case 'spring':
      return calculateSpringPrice(services);
    case 'fall':
      return calculateFallPrice(services);
    case 'trimming':
      return calculateTrimmingPrice(services);
    case 'turf':
      return calculateTurfPrice(services, propertyInfo);
    case 'snow':
      return calculateSnowPrice(services, propertyInfo);
    default:
      return 0;
  }
};
